import { useFocusEffect, useLinkTo } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { LinearGradient } from 'expo-linear-gradient';
import * as WebBrowser from 'expo-web-browser';
import { useMobileOrDesktop } from 'hoc/mobileOrDesktop';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { calcSizeForDevice, calcSizeForTv } from 'utils/index';
import { Platform } from 'utils/platform';
import { modifyVastUrl } from 'utils/vast';

import { heroStyle } from './ProgramScreen';

import NewGradientButton from 'components/Button/NewGradienteButton';
import Hero from 'components/Hero';
import { Icons } from 'components/Icons';
import Player from 'components/Player';
import SpallaPlayerPlatform from 'components/Player/SpallaPlayer/SpallaPlayerPlatform';
import { useStream } from 'components/Player/Stream/context';
import { usePlayer } from 'components/Player/context';
import ProgramContent from 'components/Program/Content';
import ProgramHeader from 'components/Program/Header';
import ProgramRelated from 'components/Program/Related';
import ProgramShared from 'components/Program/Shared';
import { useProgram } from 'components/Program/context';

const ExternalProgram: React.FC<RootStackScreenProps<'Program'>> = ({ navigation }) => {
  const { isPlaying, isFullScreen, isStreaming, pause, play, setIsVisible, mute, setNextTrack } =
    usePlayer();
  const analytics = useAnalytics();
  const program = useProgram();
  const { ads } = useGlobalConfig();
  const { isConnected } = useStream();
  const [isActive, setIsActive] = useState(false);
  const [mediaId, setMediaId] = useState<string>();
  const linkTo = useLinkTo();
  const { reset } = usePlayer();

  const { width } = useWindowDimensions();

  useEffect(() => {
    setNextTrack(undefined);
  }, []);

  const buttonDirection = !Platform.isTV && width < 720 ? 'column' : 'row';

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      if (isPlaying && !isStreaming) {
        pause();
      }
      mute(false);
    });
  }, [navigation, isPlaying, isStreaming, pause]);

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        if (!isStreaming) pause();
        setIsActive(false);
      };
    }, [isStreaming])
  );

  useFocusEffect(
    useCallback(() => {
      analytics.triggerMovieScreenPageViewEvent({
        programId: program._id,
        programTitle: program.title,
        programAgeRating: program.rating,
        programAdUnit: program.ads?.adunit?.val!,
        programYear: program?.year,
        programCategory: (program.category || []).map((tag) => tag.name).join(', '),
        programDirector: program.director,
        programGenre: (program.genre || []).map((genre) => genre.name).join(', '),
      });
    }, [])
  );

  const playerProps: PlayerProps | undefined = useMemo(() => {
    if (!program) {
      return undefined;
    }

    const media: PlayerMedia = {
      title: program.title,
      image: [program.imageVertical?.url!, program.imageHorizontal?.url!],
      duration: program.duration,
      programId: program._id,
      programTitle: program.title,
      programUrl: program.url,
      ratingValue: program.rating,
      ratingDescription: program.rateDescription,
      videoTracks: [],
      mediaSource: program.mediaSource || program.spallaID,
    };

    const mediaIdDub = program.mediaIdDub;
    const mediaIdDubSpalla = program.videoIdDub;

    if (mediaIdDub) {
      media.videoTracks.push({
        language: 'dub',
        trackId: media.mediaSource ? mediaIdDubSpalla : mediaIdDub,
      });
    }

    media.videoTracks.push({
      language: 'origin',
      trackId: media.mediaSource ? program.videoId || program.spallaID : program.mediaId,
    });

    return {
      media,
      isLive: false,
      adTagUrl:
        ads?.disableAds || program.ads?.disableAds
          ? undefined
          : Platform.isWebTV
          ? ads?.ctvVast
          : modifyVastUrl(ads?.vast, program),
      adsExpirationTime: ads?.adsExpirationTime,
    };
  }, [program, mediaId, isConnected]);

  async function redirectToExternalLink(url?: string) {
    if (!url) {
      console.error('URL inválida ou não definida');
      return;
    }
    try {
      if (Platform.OS === 'web') {
        window.open(url, '_blank');
      } else {
        await WebBrowser.openBrowserAsync(url);
      }
    } catch (error) {
      console.error('Erro ao abrir o navegador:', error);
    }
  }

  const Video = useMemo(
    () =>
      mediaId !== undefined && isActive && playerProps !== undefined ? (
        <Player {...playerProps} startTime={0} autoplay={!Platform.isWebTV} enablePip={false} />
      ) : undefined,
    [mediaId, isActive, playerProps]
  );

  const [VideoMobile, VideoDesktop] = useMobileOrDesktop(Video, Video);

  const showVideo = VideoMobile !== undefined;
  const showHero = Platform.isAndroidTV || Platform.OS === 'web' || !isFullScreen;

  useEffect(() => {
    if (!mediaId) setMediaId(program.mediaId || program.videoId);
  }, [program, mediaId]);

  return (
    <>
      {showHero && (
        <Hero program={program} hideCover={mediaId !== undefined}>
          {showVideo && (
            <SpallaPlayerPlatform
              videoId={program.videoId}
              videoIdDub={program.videoIdDub}
              playerDuration={program.duration}
              autoplay={true}
            />
          )}

          <View style={heroStyle.container}>
            <Hero.Header
              program={program}
              mediaId={mediaId}
              Aside={
                VideoDesktop !== undefined && !Platform.isAndroidTV ? (
                  <View style={[styles.videoBox, { width: Platform.isTV ? 0 : '80%' }]}>
                    {Platform.isWebTV ? (
                      <>{Video}</>
                    ) : (
                      <SpallaPlayerPlatform
                        videoId={program.videoId}
                        videoIdDub={program.videoIdDub}
                        playerDuration={program.duration}
                        autoplay={true}
                      />
                    )}
                  </View>
                ) : undefined
              }>
              <ProgramHeader />

              <View style={[styles.buttonRow, { flexDirection: buttonDirection }]}>
                {program.mediaPartner?.isActiveMediaPartnerButton && (
                  <>
                    {Platform.isAndroidTV ? (
                      <NewGradientButton
                        useTVFocus
                        width={300}
                        height={35}
                        hasTVPreferredFocus
                        style={{
                          display: mediaId && !Platform.isTV ? 'none' : undefined,
                          borderRadius: 20,
                        }}
                        onPress={() => {
                          if (!mediaId) setMediaId(program.mediaId || program.videoId);

                          analytics.triggerMediaPlayActionEvent({
                            programAdUnit: program.ads?.adunit?.val!,
                            programYear: program?.year,
                            programCategory: program?.category
                              ?.map((tag: CmsTag) => tag.name)
                              .join(', '),
                            programGenre: program?.genre
                              ?.map((genre: CmsTag) => genre.name)
                              .join(', '),
                            programDirector: program?.director,
                            programId: program?._id,
                            programTitle: program?.title,
                            programAgeRating: program?.rating,
                          });

                          play();

                          if (Platform.isAndroidTV) setIsVisible(true);
                        }}>
                        <Text style={[styles.buttonTextDark, { marginLeft: 8 }]}>
                          {program.mediaPartner?.MidiaButtonLabel || 'Assistir Agora'}
                        </Text>
                      </NewGradientButton>
                    ) : (
                      <NewGradientButton
                        style={{
                          display: mediaId && !Platform.isTV ? 'none' : undefined,
                          width: calcSizeForDevice(300, 400),
                          height: calcSizeForDevice(40, 60),
                          borderRadius: 20,
                          flexDirection: 'row',
                        }}
                        onPress={() => {
                          analytics.triggerMediaPlayActionEvent({
                            programAdUnit: program.ads?.adunit?.val!,
                            programYear: program?.year,
                            programCategory: program?.category
                              ?.map((tag: CmsTag) => tag.name)
                              .join(', '),
                            programGenre: program?.genre
                              ?.map((genre: CmsTag) => genre.name)
                              .join(', '),
                            programDirector: program?.director,
                            programId: program?._id,
                            programTitle: program?.title,
                            programAgeRating: program?.rating,
                          });

                          play();

                          if (Platform.isTV) setIsVisible(true);
                        }}>
                        <Icons.Play color={'#070D31'} />
                        <Text style={[styles.buttonTextDark, { marginLeft: 4 }]}>
                          {program.mediaPartner?.MidiaButtonLabel || 'Assistir Agora'}
                        </Text>
                      </NewGradientButton>
                    )}
                  </>
                )}

                {!Platform.isTV && (
                  <Pressable
                    style={[
                      styles.outlineButton,
                      {
                        paddingHorizontal: calcSizeForDevice(50, 70),
                        height: calcSizeForDevice(40, 52),
                      },
                    ]}
                    onPress={() => {
                      analytics.triggerEventExternalLink({
                        mediaType: 'video',
                        programId: program._id,
                        programTitle: program.title,
                        programAgeRating: program.rating,
                        ClickToExternalLink: program.mediaPartner?.PartnerUrl,
                      });

                      redirectToExternalLink(
                        program.mediaPartner?.PartnerUrl
                          ? program.mediaPartner.PartnerUrl
                          : '/categorias/novidades'
                      );
                      if (Platform.OS === 'android' || Platform.OS === 'ios') {
                        reset({ media: undefined });
                        navigation.goBack();
                      }
                    }}>
                    <Icons.ClapperboardPlay color={'#fff'} />
                    <Text style={styles.buttonTextLight}>
                      {program.mediaPartner?.PartnerLinkLabel || 'Confira as novidades'}
                    </Text>
                  </Pressable>
                )}

                {!program.mediaPartner?.isActiveMediaPartnerButton && Platform.isTV && (
                  <>
                    {Platform.isAndroidTV ? (
                      <NewGradientButton
                        useTVFocus
                        hasTVPreferredFocus
                        width={200}
                        onPress={() => linkTo('/categorias/novidades')}>
                        <Text style={[styles.buttonTextBold, { fontSize: calcSizeForTv(24) }]}>
                          NOVIDADES BANDPLAY
                        </Text>
                      </NewGradientButton>
                    ) : (
                      <Pressable
                        style={[
                          styles.transparentButton,
                          {
                            width: calcSizeForDevice(300, 400),
                            height: calcSizeForDevice(40, 52),
                          },
                        ]}
                        onPress={() => linkTo('/categorias/novidades')}>
                        <LinearGradient
                          colors={['#6DFFBC', '#00AD5C']}
                          start={{ x: 0, y: 0 }}
                          end={{ x: 0, y: 1 }}
                          style={{
                            width: calcSizeForDevice(300, 400),
                            height: calcSizeForDevice(40, 52),
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 50,
                          }}>
                          <Text style={[styles.buttonTextBold, { fontSize: calcSizeForTv(18) }]}>
                            NOVIDADES BANDPLAY
                          </Text>
                        </LinearGradient>
                      </Pressable>
                    )}
                  </>
                )}
              </View>
              <View style={styles.sharedContainer}>
                <ProgramShared />
              </View>
              <ProgramContent />
            </Hero.Header>

            <View style={styles.relatedContainer}>
              <ProgramRelated />
            </View>
          </View>
        </Hero>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  videoContainer: {
    flex: 1,
  },
  videoBox: {
    aspectRatio: 1 / 0.56,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 16,
  },
  sharedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: calcSizeForDevice(10, 14),
    marginBottom: calcSizeForDevice(10, 20),
  },
  relatedContainer: {
    paddingVertical: calcSizeForDevice(24),
  },
  buttonTextDark: {
    color: '#070D31',
    fontWeight: 'bold',
  },
  buttonTextLight: {
    color: '#fff',
    marginLeft: 8,
    fontWeight: 'bold',
  },
  buttonTextBold: {
    fontWeight: 'bold',
    color: '#070d31',
    textTransform: 'uppercase',
  },
  outlineButton: {
    borderWidth: 1,
    borderColor: '#2240FF',
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  transparentButton: {
    backgroundColor: 'transparent',
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ExternalProgram;
